/**
 * CookieConsentBanner — GDPR / EU-compliant analytics consent banner.
 *
 * Wires into Google Consent Mode v2: until the user clicks "Accept",
 * GA4 stays in denied state (no analytics_storage, no ad_storage).
 * Strictly-necessary cookies (auth, language) are unaffected — those
 * fall outside GDPR's consent requirement.
 *
 * Renders only when no consent decision has been stored yet, so
 * returning visitors who have already chosen never see the banner.
 */
import { useEffect, useState } from "react";
import { ShieldCheck, X } from "lucide-react";
import { Link } from "react-router-dom";
import { getStoredConsent, setConsent } from "../lib/analytics";
import { useLanguage } from "../contexts/LanguageContext";

const COPY = {
  sk: {
    title: "Súhlas s cookies",
    body:
      "Používame nevyhnutné cookies (prihlásenie, jazyk) a — len s vaším súhlasom — analytické cookies (Google Analytics 4) na zlepšenie služby. IP adresa je anonymizovaná.",
    learn: "Pravidlá cookies",
    decline: "Iba nevyhnutné",
    accept: "Prijať všetky",
    close: "Zavrieť",
  },
  en: {
    title: "Cookie preferences",
    body:
      "We use strictly necessary cookies (login, language) and — only with your consent — analytics cookies (Google Analytics 4) to improve the service. Your IP is anonymised.",
    learn: "Cookie policy",
    decline: "Necessary only",
    accept: "Accept all",
    close: "Close",
  },
  cs: {
    title: "Souhlas s cookies",
    body:
      "Používáme nezbytné cookies (přihlášení, jazyk) a — jen s vaším souhlasem — analytické cookies (Google Analytics 4) ke zlepšení služby. IP adresa je anonymizovaná.",
    learn: "Zásady cookies",
    decline: "Jen nezbytné",
    accept: "Přijmout vše",
    close: "Zavřít",
  },
};

export default function CookieConsentBanner() {
  const [visible, setVisible] = useState(false);
  const { language } = useLanguage();
  const path = window.location.pathname;
  const proposal = path.startsWith("/navrh-webu");
  const csRoute = /^\/(gratulujeme\/cz|tvorba-webu\/cz|cz\/)/.test(path) || (proposal && document.documentElement.lang === "cs");
  const skRoute = proposal || /^\/(pre-|agency|sluzby|riesenia|vsetko-v-jednom|bezpecnost-webu|gratulujeme\/sk|tvorba-webu\/sk|rebricek-zlepseni)/.test(path);
  const siteLang = (language || document.documentElement.lang || "").toLowerCase();
  const lang = csRoute ? "cs" : skRoute || siteLang.startsWith("sk") ? "sk" : siteLang.startsWith("cs") ? "cs" : "en";
  const t = COPY[lang];

  useEffect(() => {
    // Banner is shown only on the very first visit OR after the user
    // explicitly cleared their cookies. After that, the choice persists.
    if (getStoredConsent() === null) {
      // Slight delay so it doesn't compete with the page's hero animation.
      const id = setTimeout(() => setVisible(true), 600);
      return () => clearTimeout(id);
    }
    return undefined;
  }, []);

  if (!visible) return null;

  const handleChoice = (state) => {
    setConsent(state);
    setVisible(false);
  };

  return (
    <div
      data-testid="cookie-consent-banner"
      role="dialog"
      aria-labelledby="cookie-consent-title"
      className="fixed inset-x-0 bottom-0 z-[9999] px-3 pb-3 sm:px-6 sm:pb-6"
    >
      <div className="max-w-4xl mx-auto bg-[#0f0f12]/95 backdrop-blur-md border border-zinc-700 rounded-xl shadow-2xl shadow-black/60">
        <div className="flex flex-col sm:flex-row gap-4 p-4 sm:p-5">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <div className="shrink-0 mt-0.5 w-9 h-9 rounded-full bg-[#FFE600]/10 border border-[#FFE600]/30 flex items-center justify-center">
              <ShieldCheck className="w-4 h-4 text-[#FFE600]" />
            </div>
            <div className="min-w-0">
              <h2
                id="cookie-consent-title"
                className="text-sm font-bold text-white mb-1"
              >
                {t.title}
              </h2>
              <p className="text-[12.5px] leading-relaxed text-zinc-400">
                {t.body}{" "}
                <Link
                  to="/cookies"
                  className="text-[#FFE600] hover:underline whitespace-nowrap"
                  data-testid="cookie-consent-policy-link"
                >
                  {t.learn} →
                </Link>
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 sm:flex-col sm:items-stretch sm:justify-center sm:shrink-0">
            <button
              type="button"
              onClick={() => handleChoice("denied")}
              data-testid="cookie-consent-decline-btn"
              className="flex-1 sm:flex-none px-4 py-2 text-xs font-bold text-zinc-300 bg-zinc-800 hover:bg-zinc-700 rounded-lg transition-colors whitespace-nowrap"
            >
              {t.decline}
            </button>
            <button
              type="button"
              onClick={() => handleChoice("granted")}
              data-testid="cookie-consent-accept-btn"
              className="flex-1 sm:flex-none px-4 py-2 text-xs font-bold text-black bg-[#FFE600] hover:bg-[#FFF066] rounded-lg transition-colors whitespace-nowrap"
            >
              {t.accept}
            </button>
          </div>

          <button
            type="button"
            onClick={() => handleChoice("denied")}
            aria-label={t.close}
            className="hidden sm:flex absolute top-2 right-2 w-7 h-7 items-center justify-center text-zinc-500 hover:text-white"
            data-testid="cookie-consent-close-btn"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
